import React from "react";
import { Logo } from "./Logo";

/**
 * OgGenerator — a hidden 1200x630 layout used to produce /public/og-image.png
 * via a headless screenshot. Not linked from nav, robots, or sitemap.
 *
 * Route: /og-generator
 * The layout is absolutely sized to 1200x630 to match Open Graph spec.
 */
export default function OgGenerator() {
  return (
    <div
      style={{
        width: 1200,
        height: 630,
        background: "#0a0a0a",
        color: "#ffffff",
        fontFamily:
          '"Oswald", "IBM Plex Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        position: "relative",
        overflow: "hidden",
      }}
      data-testid="og-card"
    >
      {/* Grid lines background */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(255,234,0,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,234,0,0.05) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
          opacity: 0.6,
        }}
      />

      {/* Radial neon glow */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse at 70% 40%, rgba(255,234,0,0.12), transparent 60%)",
        }}
      />

      {/* Grain */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          opacity: 0.05,
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")",
          mixBlendMode: "overlay",
        }}
      />

      {/* Top hazard stripe bar */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 14,
          backgroundImage:
            "repeating-linear-gradient(45deg, #FFEA00 0, #FFEA00 20px, #0a0a0a 20px, #0a0a0a 40px)",
        }}
      />

      {/* Bottom hazard stripe bar */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: 14,
          backgroundImage:
            "repeating-linear-gradient(45deg, #FFEA00 0, #FFEA00 20px, #0a0a0a 20px, #0a0a0a 40px)",
        }}
      />

      {/* Section number top-left */}
      <div
        style={{
          position: "absolute",
          top: 44,
          left: 60,
          fontFamily: '"IBM Plex Mono", monospace',
          fontSize: 12,
          letterSpacing: "0.3em",
          color: "#A3A3A3",
          textTransform: "uppercase",
        }}
      >
        // OG · SHARE CARD
      </div>

      {/* Top-right URL */}
      <div
        style={{
          position: "absolute",
          top: 44,
          right: 60,
          fontFamily: '"IBM Plex Mono", monospace',
          fontSize: 12,
          letterSpacing: "0.3em",
          color: "#FFEA00",
          textTransform: "uppercase",
        }}
      >
        DUUNARI-APP.FI
      </div>

      {/* Main content — flex row */}
      <div
        style={{
          position: "absolute",
          top: 100,
          left: 60,
          right: 60,
          bottom: 100,
          display: "flex",
          alignItems: "center",
          gap: 56,
        }}
      >
        {/* Left: Logo with aura */}
        <div
          style={{
            flexShrink: 0,
            width: 220,
            height: 220,
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Neon aura */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: -30,
              background:
                "radial-gradient(circle, rgba(255,234,0,0.55) 0%, rgba(255,234,0,0.18) 45%, rgba(255,234,0,0) 75%)",
              filter: "blur(24px)",
            }}
          />
          {/* Border frame */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              border: "3px solid #FFEA00",
              transform: "rotate(-6deg)",
              boxShadow: "6px 6px 0 0 rgba(255,234,0,0.15)",
            }}
          />
          <Logo size={180} aura={false} testId="og-logo" />
        </div>

        {/* Right: Brand + tagline + pills */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Eyebrow */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              border: "2px solid rgba(255,234,0,0.4)",
              background: "rgba(0,0,0,0.4)",
              padding: "8px 14px",
              marginBottom: 22,
            }}
          >
            <span
              style={{
                display: "inline-block",
                width: 8,
                height: 8,
                background: "#FFEA00",
              }}
              aria-hidden="true"
            />
            <span
              style={{
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: 12,
                letterSpacing: "0.3em",
                color: "#FFEA00",
                textTransform: "uppercase",
              }}
            >
              Rakennettu työmaalle · Made in Pornainen
            </span>
          </div>

          {/* Brand name */}
          <div
            style={{
              fontFamily: '"Oswald", sans-serif',
              fontWeight: 700,
              fontSize: 108,
              lineHeight: 0.9,
              letterSpacing: "-0.02em",
              color: "#ffffff",
              textTransform: "uppercase",
              marginBottom: 18,
            }}
          >
            DUUNARI <span style={{ color: "#FFEA00" }}>APP</span>
          </div>

          {/* Tagline */}
          <div
            style={{
              fontFamily: '"Oswald", sans-serif',
              fontWeight: 500,
              fontSize: 30,
              lineHeight: 1.15,
              letterSpacing: "-0.005em",
              color: "#D4D4D4",
              textTransform: "uppercase",
              marginBottom: 28,
              maxWidth: 720,
            }}
          >
            Sovellus, joka listaa{" "}
            <span
              style={{
                color: "#FFEA00",
                position: "relative",
                display: "inline-block",
              }}
            >
              työtunnit
              <span
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: -6,
                  height: 5,
                  backgroundImage:
                    "repeating-linear-gradient(45deg, #FFEA00 0, #FFEA00 6px, transparent 6px, transparent 12px)",
                }}
              />
            </span>{" "}
            puolestasi.
          </div>

          {/* Feature pills */}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {["AI · LÄHETELUKU", "< 5s · TUNTIKIRJAUS", "PDF · RAPORTIT", "24/7 · PILVI"].map(
              (label) => (
                <span
                  key={label}
                  style={{
                    display: "inline-block",
                    border: "2px solid #FFEA00",
                    background: "rgba(255,234,0,0.06)",
                    color: "#FFEA00",
                    padding: "8px 14px",
                    fontFamily: '"Oswald", sans-serif',
                    fontWeight: 700,
                    fontSize: 15,
                    letterSpacing: "0.2em",
                    textTransform: "uppercase",
                  }}
                >
                  {label}
                </span>
              ),
            )}
          </div>
        </div>
      </div>

      {/* Bottom-right store cue */}
      <div
        style={{
          position: "absolute",
          bottom: 44,
          right: 60,
          fontFamily: '"Oswald", sans-serif',
          fontWeight: 700,
          fontSize: 16,
          letterSpacing: "0.25em",
          textTransform: "uppercase",
          color: "#A3A3A3",
        }}
      >
        <span style={{ color: "#FFEA00" }}>▸</span> Google Play &nbsp;·&nbsp;{" "}
        <span style={{ color: "#FFEA00" }}>▸</span> App Store
      </div>

      {/* Bottom-left version chip */}
      <div
        style={{
          position: "absolute",
          bottom: 44,
          left: 60,
          fontFamily: '"IBM Plex Mono", monospace',
          fontSize: 11,
          letterSpacing: "0.3em",
          color: "#A3A3A3",
          textTransform: "uppercase",
        }}
      >
        v1.0 · 2026
      </div>

      {/* Corner marks */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 14,
          left: 14,
          width: 14,
          height: 14,
          background: "#FFEA00",
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 14,
          right: 14,
          width: 14,
          height: 14,
          background: "#FFEA00",
        }}
      />
    </div>
  );
}
